import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { LogoMark } from "../components/Logo";
import { ArrowRight, Mail, Lock, User as UserIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("login"); // "login" | "register" | "google"
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const endpoint = mode === "register" ? "/auth/register" : "/auth/login";
      const body =
        mode === "register"
          ? { email: form.email, password: form.password, name: form.name }
          : { email: form.email, password: form.password };
      const { data } = await axios.post(`${API}${endpoint}`, body, { withCredentials: true });
      setUser(data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  let submitLabel = t.auth.sign_in_btn;
  if (busy) submitLabel = t.common.loading;
  else if (mode === "register") submitLabel = t.auth.create_account_btn;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-16 bg-[#F8F9FA]">
      <div className="w-full max-w-md bg-white border border-neutral-200 p-8 sm:p-10">
        <div className="w-fit mb-6">
          <LogoMark size={48} />
        </div>

        <h1 className="text-3xl font-black tracking-tighter mb-2" data-testid="login-title">
          {mode === "register" ? t.auth.create_account : t.auth.sign_in_title}
        </h1>
        <p className="text-sm text-neutral-600 mb-7">
          {mode === "register" ? t.auth.create_account_subtitle : t.auth.sign_in_subtitle}
        </p>

        {/* Mode tabs */}
        <div className="grid grid-cols-2 gap-0 mb-6 border border-neutral-200">
          <button
            type="button"
            onClick={() => { setMode("login"); setError(""); }}
            className={`py-2.5 text-sm font-bold tracking-wide ${mode !== "register" ? "bg-neutral-900 text-white" : "bg-white text-neutral-600"}`}
            data-testid="login-tab-signin"
          >
            {t.auth.tab_signin}
          </button>
          <button
            type="button"
            onClick={() => { setMode("register"); setError(""); }}
            className={`py-2.5 text-sm font-bold tracking-wide ${mode === "register" ? "bg-neutral-900 text-white" : "bg-white text-neutral-600"}`}
            data-testid="login-tab-register"
          >
            {t.auth.tab_register}
          </button>
        </div>

        <form onSubmit={submitPassword} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                required
                placeholder={t.booking.name}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-neutral-300 ps-10 pe-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
                data-testid="login-name"
              />
            </div>
          )}
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="email"
              required
              placeholder={t.booking.email}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-neutral-300 ps-10 pe-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
              data-testid="login-email"
            />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="password"
              required
              minLength={mode === "register" ? 8 : 1}
              placeholder={t.auth.password}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full border border-neutral-300 ps-10 pe-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#FF5A00]"
              data-testid="login-password"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}

          {mode !== "register" && (
            <div className="text-end">
              <Link to="/forgot-password" className="text-xs font-semibold text-neutral-600 hover:text-[#FF5A00] transition-colors" data-testid="forgot-password-link">
                {t.auth.forgot_password}
              </Link>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full btn-primary justify-center disabled:opacity-60"
            data-testid="login-submit-btn"
          >
            {submitLabel}
            <ArrowRight size={18} />
          </button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-neutral-400">
          <div className="h-px flex-1 bg-neutral-200" />
          <span>{t.auth.or}</span>
          <div className="h-px flex-1 bg-neutral-200" />
        </div>

        <button
          onClick={handleGoogle}
          className="w-full justify-center inline-flex items-center gap-3 border-2 border-neutral-300 hover:border-neutral-900 px-6 py-3 text-sm font-bold tracking-wide transition-colors bg-white"
          data-testid="google-signin-btn"
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          {t.auth.sign_in}
        </button>

        <p className="text-xs text-neutral-500 mt-6 text-center">
          <Link to="/" className="hover:text-neutral-900 underline-offset-4 hover:underline">{t.nav.home}</Link>
        </p>
      </div>
    </div>
  );
}
